<template>
  <section class="glass-panel flex flex-col gap-4 rounded-xl p-5">
    <header class="relative flex flex-col items-start gap-1.5 pr-16 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:pr-0">
      <h2 class="m-0 text-sm font-semibold text-foreground">
        {{ chartTitle }}
      </h2>

      <div class="flex items-center gap-5">
        <div class="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
          <span class="flex items-center gap-1.5">
            <i
              class="h-1 w-5.5 shrink-0 rounded-full"
              :style="{ background: legendColor(dataset1Color) }"
            />
            <span>{{ tr(dataset1Label) }}<template v-if="dataset1Unit"> ({{ tr(dataset1Unit) }})</template></span>
          </span>

          <span v-if="dataset2Label" class="flex items-center gap-1.5">
            <i
              class="h-1 w-5.5 shrink-0 rounded-full"
              :style="{ background: legendColor(dataset2Color) }"
            />
            <span>{{ tr(dataset2Label) }}<template v-if="dataset2Unit"> ({{ tr(dataset2Unit) }})</template></span>
          </span>
        </div>

        <div class="absolute top-0 right-0 flex items-center gap-0.5 sm:static">
          <ui-button
            v-for="type in chartTypes"
            :key="type.value"
            variant="ghost"
            size="icon-sm"
            :aria-label="type.label"
            :aria-pressed="chartType === type.value"
            :class="chartType === type.value ? 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary' : 'text-muted-foreground'"
            @click="() => switchChartType(type.value)"
          >
            <component :is="type.icon" />
          </ui-button>
        </div>
      </div>
    </header>

    <div ref="chartWrapper" class="relative h-50 w-full">
      <svg v-if="chartType == 'line'" ref="lineChart" :class="[ 'size-full', isEmpty && 'invisible' ]"></svg>
      <svg v-else-if="chartType == 'bar'" ref="barChart" :class="[ 'size-full', isEmpty && 'invisible' ]"></svg>

      <div v-if="isEmpty" class="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground">
        <chart-line-icon class="size-6" />
        {{ tr('No data for the selected period.') }}
      </div>
    </div>
  </section>
</template>

<script>
import { ChartColumn as ChartColumnIcon, ChartLine as ChartLineIcon } from '@lucide/vue';
import * as d3 from 'd3';
import { markRaw } from 'vue';

import BarChart from '@/chart/chartBar';
import ChartCircle from '@/chart/chartCircle';
import ChartDataDetails from '@/chart/chartDataDetails';
import ChartLine from '@/chart/chartLine';
import ChartTooltip from '@/chart/chartTooltip';
import LineChart from '@/chart/lineChart';
import { formatToStringMD, getNumberOfDays } from '@/utils/date';
import { formatCompactNumber } from '@/utils/number';

const CHART_TYPE_LINE = 'line';
const CHART_TYPE_BAR = 'bar';

const dataValidator = v => {
  if (!v || v.length === 0) {
    return true;
  }

  let allValid = true;
  v.forEach(item => {
    if (
      !(
        typeof item.value === 'number' &&
        (typeof item.date !== 'undefined' || typeof item.label !== 'undefined')
      )
    ) {
      allValid = false;
    }
  });

  return allValid;
};

export default {
  name: 'DashboardDualChart',
  components: {
    ChartLineIcon,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    dateFrom: {
      type: Date,
      required: true,
      default: null,
    },
    dateTo: {
      type: Date,
      required: true,
      default: null,
    },
    dataset1: {
      type: Array,
      required: true,
      validator: dataValidator,
    },
    dataset2: {
      type: Array,
      required: false,
      default: () => [],
      validator: dataValidator,
    },
    dataset1Label: {
      type: String,
      default: '',
    },
    dataset2Label: {
      type: String,
      default: '',
    },
    dataset1Unit: {
      type: String,
      default: '',
    },
    dataset2Unit: {
      type: String,
      default: '',
    },
    chartTitle: {
      type: String,
      default: '',
    },
    dataset1Color: {
      type: String,
      default: 'primary',
    },
    dataset2Color: {
      type: String,
      default: 'black',
    },
  },
  data() {
    return {
      chartTypes: [
        { value: CHART_TYPE_BAR, label: this.tr('Bar chart'), icon: markRaw(ChartColumnIcon) },
        { value: CHART_TYPE_LINE, label: this.tr('Line chart'), icon: markRaw(ChartLineIcon) },
      ],
      chartWidth: 465,
      chartHeight: 180,
      chartXAxisLabelMaxWidth: 40,
      chartType: CHART_TYPE_LINE,
      resizeObserver: null,
      loadingCounter: 0,
      loadingTimer: null,
    };
  },
  computed: {
    // Nothing but zeros: an empty state reads better than a flat axis.
    isEmpty() {
      return !this.isLoading && [ ...this.dataset1, ...this.dataset2 ].every(item => !item.value);
    },
    isDarkTheme() {
      return this.$store.getters['app/currentTheme'] === 'dark';
    },
    computedDataset1() {
      if (this.isLoading && this.dataset1Label) {
        return this.getLoadingData();
      }

      return this.dataset1.length !== 0 ? this.dataset1 : this.emptyDataset;
    },
    computedDataset2() {
      if (this.isLoading && this.dataset2Label) {
        return this.getLoadingData(50, 1.1);
      }

      return this.dataset2.length !== 0 ? this.dataset2 : this.emptyDataset;
    },
    hasDataset2() {
      return this.dataset2Label && this.computedDataset2 && this.computedDataset2.length > 0;
    },
    numberOfDays() {
      return getNumberOfDays(this.dateFrom, this.dateTo);
    },
    emptyDataset() {
      const result = [];

      for (let i = 1; i <= this.numberOfDays; ++i) {
        result.push({
          date: new Date(this.dateTo.getTime() - (i * 24 * 60 * 60 * 1000)),
          value: 0,
        });
      }

      return result;
    },
  },
  watch: {
    isLoading: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const fps = 60;
          this.loadingTimer = setInterval(() => ++this.loadingCounter, 1000 / fps);
        }
        else {
          if (this.loadingTimer) {
            clearInterval(this.loadingTimer);
            this.loadingTimer = null;
          }
        }
      },
    },
    '$root.language'() {
      this.updateVisibleChart();
    },
    isDarkTheme() {
      this.updateVisibleChart();
    },
  },
  mounted() {
    this.updateVisibleChart();

    this.$watch(
      vm => [ vm.computedDataset1, vm.computedDataset2 ],
      () => this.updateVisibleChart(),
      { deep: true },
    );

    this.attachResizeObserver();
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.chartWrapper) {
      this.resizeObserver.unobserve(this.$refs.chartWrapper);
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    // Same colors as the chart lines ('primary' is resolved by a CSS rule there).
    legendColor(name) {
      return { primary: 'var(--primary)', black: 'var(--foreground)' }[name] ?? name;
    },
    getNumberOfDays,
    formatToStringMD,
    themedSeriesColor(color) {
      return (color === 'black' && this.isDarkTheme) ? '#ffffff' : color;
    },
    updateVisibleChart() {
      switch (this.chartType) {
        case CHART_TYPE_LINE:
          this.renderLineChart();
          break;

        case CHART_TYPE_BAR:
          this.renderBarChart();
          break;
      }
    },
    switchChartType(type) {
      this.chartType = type;
      this.$nextTick(() => this.updateVisibleChart());
    },
    getXAxisTickValues(chartData) {
      const parsedData = chartData.map(d => ({
        ...d,
        date: typeof d.date === 'string'
          ? new Date(d.date + 'T00:00:00Z')
          : d.date,
      }));

      const dateExtent = d3.extent(parsedData, d => d.date);

      const maxNumberOfXTick = Math.floor((this.chartWidth - (this.chartType === CHART_TYPE_LINE ? 32 + 12 : 0)) / this.chartXAxisLabelMaxWidth);

      const possibleStep = Math.ceil(this.getNumberOfDays(dateExtent[0], dateExtent[1]) / maxNumberOfXTick);

      const step = Math.max(1, Math.min(possibleStep, maxNumberOfXTick));

      const start = d3.utcDay.floor(dateExtent[0]);
      const end = d3.utcDay.offset(dateExtent[1], 1);

      return d3.utcDays(start, end, step);
    },
    formatAxisValue(value) {
      if (!isNaN(parseFloat(value)) && isFinite(value)) {
        value = formatCompactNumber(value, true);
      }

      return value.toString();
    },
    renderLineChart() {
      if (!this.computedDataset1.length) {
        return;
      }

      const chartOptions = {
        xAxisTicks: this.getXAxisTickValues(this.computedDataset1),
        xAxisFormat: d => this.formatToStringMD(d, this.tr),
        xScaleType: 'scaleUtc',
        yAxisFormat: d => this.formatAxisValue(d),
        isDarkTheme: this.isDarkTheme,
      };

      const configSets = [
        {
          dataset: this.computedDataset1,
          color: this.dataset1Color,
          unit: this.dataset1Unit,
        },
      ];
      if (this.hasDataset2) {
        configSets.push({
          dataset: this.computedDataset2,
          color: this.dataset2Color,
          unit: this.dataset2Unit,
        });

        chartOptions.y2AxisFormat = d => this.formatAxisValue(d);
      }

      const chart = new LineChart(
        this.$refs.lineChart,
        this.chartWidth,
        this.chartHeight,
        chartOptions,
      );

      for (const index in configSets) {
        const configSet = configSets[index];
        const parsedDataset = configSet.dataset.map(d => ({
          ...d,
          date: typeof d.date === 'string' ? new Date(d.date + 'T00:00:00Z') : d.date,
        }));

        const data = new ChartDataDetails(parsedDataset, 'date', 'value');
        data.color = this.themedSeriesColor(configSet.color);

        if (!this.isLoading) {
          data.tooltip = new ChartTooltip(
            chart,
            configSet.unit,
            configSet.unit,
            { yPosFixed: index == 0 ? 6 : 40 },
          );
          data.tooltip.position = 'top';
          if (this.isDarkTheme && configSet.color === 'black') {
            data.tooltip.textColor = '#222027';
          }
        }

        if (index == 0) {
          chart.addData(data);
        }
        else {
          chart.addDataSecond(data);
        }
      }

      this.$refs.lineChart.innerHTML = '';
      chart.render();

      if (!this.isLoading) {
        for (const index in configSets) {
          const configSet = configSets[index];
          const data = index == 0 ? chart.data[0] : chart.dataSecond[0];

          const dataSetOnHoverCircle = new ChartCircle(chart);
          dataSetOnHoverCircle.stroke = this.themedSeriesColor(configSet.color);
          data.circleOnHoverElement = dataSetOnHoverCircle.draw();

          if (index == 0) {
            const hoverVerticalLine = new ChartLine(chart);
            hoverVerticalLine.stroke = this.themedSeriesColor(configSet.color);
            hoverVerticalLine.y2 = 'calc(100% - 20px)';
            data.verticalLineOnHoverElement = hoverVerticalLine.draw();
          }
        }
      }
    },
    renderBarChart() {
      if (!this.computedDataset1.length) {
        return;
      }

      const configSets = [
        {
          dataset: this.computedDataset1,
          group: 'dataset1',
          color: this.dataset1Color,
          unit: this.dataset1Unit,
        },
      ];
      if (this.hasDataset2) {
        configSets.push({
          dataset: this.computedDataset2,
          group: 'dataset2',
          color: this.dataset2Color,
          unit: this.dataset2Unit,
        });
      }

      const chartOptions = {
        xAxisTicks: this.getXAxisTickValues(this.computedDataset1),
        xAxisFormat: d => this.formatToStringMD(d, this.tr),
        xScaleType: 'scaleUtc',
        isDarkTheme: this.isDarkTheme,
      };

      const chart = new BarChart(
        this.$refs.barChart,
        this.chartWidth,
        this.chartHeight,
        chartOptions,
      );

      for (const index in configSets) {
        const configSet = configSets[index];
        const dataSet = new ChartDataDetails(configSet.dataset, 'date', 'value');
        dataSet.group = configSet.group;
        dataSet.color = this.themedSeriesColor(configSet.color);

        if (!this.isLoading) {
          dataSet.tooltip = new ChartTooltip(
            chart,
            configSet.unit,
            configSet.unit,
            { xPosOffset: index == 0 ? -32 : 32 },
          );
          dataSet.tooltip.position = 'top';
          if (this.isDarkTheme && configSet.color === 'black') {
            dataSet.tooltip.textColor = '#222027';
          }
        }

        chart.addData(dataSet);
      }

      this.$refs.barChart.innerHTML = '';
      chart.render();
    },
    getLoadingData(positionModifier, speedModifier) {
      const aDayMs = 24 * 60 * 60 * 1000;

      const frequency = 0.3;
      const speed = 0.04 * (speedModifier ?? 1);

      const dateEnd = new Date(this.dateTo.getTime()).setHours(0, 0, 0, 0);

      const loadingData = [];
      const ticks = this.chartType === CHART_TYPE_LINE ? 30 : this.numberOfDays;
      const maxValue = 1000;
      for (let i = ticks; i > 0; --i) {
        let value = (Math.sin(frequency * i + speed * (this.loadingCounter + (positionModifier ?? 0))) * 0.5 + 0.5) * maxValue;
        if (value > maxValue * 0.995) {
          value = maxValue;
        }

        let date = null;
        if (i === ticks) {
          date = this.dateFrom;
        }
        else if (i === 1) {
          date = new Date(dateEnd);
        }
        else {
          date = new Date(dateEnd - (i * aDayMs) * ((this.numberOfDays - 1) / ticks));
        }

        loadingData.push({
          date,
          value,
        });
      }

      return loadingData;
    },
    recalculateAndUpdateChart() {
      const chartWrapperEl = this.$refs.chartWrapper;
      if (chartWrapperEl) {
        const newWidth = Math.floor(chartWrapperEl.offsetWidth);
        this.chartWidth = newWidth;
        this.updateVisibleChart();
      }
    },
    attachResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => this.recalculateAndUpdateChart());

      if (this.$refs.chartWrapper) {
        this.resizeObserver.observe(this.$refs.chartWrapper);
      }
    },
  },
};
</script>
